import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { notify } from 'reducers/NotifierReducer';
import { notifyDataUpdate } from 'reducers/DataUpdateReducer';
import { RMSCommonDialog, RMSConfirmationDialog } from 'features/shared/components/dialog';
import RMSRecordHistoryButton from 'features/shared/components/modules/RMSRecordHistoryButton';
import useConfirmDialog from 'features/shared/utility/useConfirmDialog';
import { executeApiAction } from 'features/shared/utility/apiHelper';
import { RMSMenuSidebar } from 'features/shared/components/sidebars';
import { getSelectedPlaceMenu, PlaceSidebarGroups } from '../constants/placeSideBarMenuHelper';
import { PlaceMenuItemKey } from '../constants/placeMenuItemKeys';
import { getPlaceSops } from '../services/sopService';
import { usePlaceDialog } from '../hooks/usePlaceDialog';
import { placeSaved } from '../store/actions/placeDialog.actions';

const AddEditPlaceDialog = ({ dialog }) => {
  const dispatch = useDispatch();
  const { closePlaceDialog } = usePlaceDialog();
  const { requestId, closeOnSave, initialMenuKey, initialAddressId } = dialog;
  const detailsRef = useRef(null);

  const [placeId, setPlaceId] = useState(dialog.placeId);
  const [activeKey, setActiveKey] = useState(() =>
    dialog.placeId && getSelectedPlaceMenu(initialMenuKey)
      ? initialMenuKey
      : PlaceMenuItemKey.EDIT_PLACE
  );
  const [formState, setFormState] = useState({
    isDirty: false,
    submitting: false,
    loading: false,
    canSave: false,
  });
  const [record, setRecord] = useState(null);
  const [sopCount, setSopCount] = useState(0);
  const [sopRefreshKey, setSopRefreshKey] = useState(0);

  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const activeMenu = getSelectedPlaceMenu(activeKey);
  const ActiveContent = activeMenu?.content;
  const isDetails = activeKey === PlaceMenuItemKey.EDIT_PLACE;
  const busy = formState.submitting || formState.loading;

  const permissions = usePermissions(PERMISSION_RESOURCES.PLACE);
  const isViewOnly = Boolean(placeId && !permissions.Edit);

  const handleStateChange = useCallback((state) => {
    setFormState(state);
    if (state.recordHistory) setRecord(state.recordHistory);
  }, []);

  const handleSaved = useCallback(
    (place) => {
      dispatch(placeSaved(place, { requestId, isEditMode: Boolean(placeId) }));
      dispatch(
        notifyDataUpdate({
          type: 'Place',
          data: { action: placeId ? 'Update' : 'Create', ptsPlaceID: place?.ptsPlaceID },
        })
      );
      setRecord(place);
      if (closeOnSave) closePlaceDialog(requestId);
      else if (place?.ptsPlaceID) setPlaceId(place.ptsPlaceID);
    },
    [dispatch, requestId, placeId, closeOnSave, closePlaceDialog]
  );

  const handleMenuSelect = useCallback(
    (nextKey) => {
      if (nextKey === activeKey || busy) return;
      if (!placeId && nextKey !== PlaceMenuItemKey.EDIT_PLACE) {
        dispatch(notify('Save the place first to access its other menus.', 'warning'));
        return;
      }
      const changeMenu = () => {
        setActiveKey(nextKey);
        setFormState({ isDirty: false, submitting: false, loading: false, canSave: false });
      };
      if (isDetails && detailsRef.current?.isDirty?.()) {
        requestConfirm(
          'You have unsaved changes on this place. Are you sure you want to leave without saving?',
          changeMenu,
          'Unsaved Changes'
        );
        return;
      }
      changeMenu();
    },
    [activeKey, busy, placeId, isDetails, requestConfirm, dispatch]
  );

  const handleClose = useCallback(() => {
    if (busy) return;
    const close = () => closePlaceDialog(requestId);
    if (isDetails && detailsRef.current?.isDirty?.()) {
      requestConfirm(
        'You have unsaved changes on this place. Are you sure you want to close without saving?',
        close,
        'Unsaved Changes'
      );
      return;
    }
    close();
  }, [busy, isDetails, closePlaceDialog, requestId, requestConfirm]);

  const handleSopsChanged = useCallback(() => setSopRefreshKey((key) => key + 1), []);
  useEffect(() => {
    let active = true;
    setSopCount(0);
    if (!placeId) return undefined;
    executeApiAction({
      dispatch,
      action: () => getPlaceSops(placeId),
      onSuccess: (rows) => {
        if (active) setSopCount((rows || []).length);
      },
    });
    return () => {
      active = false;
    };
  }, [placeId, sopRefreshKey, dispatch]);

  return (
    <>
      <RMSCommonDialog
        open
        fullScreen
        fullWidth
        maxWidth="xl"
        title={isViewOnly ? 'View Place' : placeId ? 'Edit Place' : 'Add Place'}
        onClose={handleClose}
        closeOnBackdropClick={false}
        className="add-edit-place-page-content"
        dialogClassName="add-edit-place-dialog"
        onSave={() => detailsRef.current?.save?.()}
        loading={busy}
        saveDisabled={busy || !formState.isDirty || !formState.canSave}
        saveButton={{ show: isDetails, type: 'simple', label: 'Save', color: 'primary' }}
        cancelButton={{ show: true, type: 'simple', label: 'Close' }}
        leftActions={
          isDetails && (
            <RMSRecordHistoryButton
              disabled={!record?.Created || busy}
              created={record?.Created}
              createdBy={record?.CreatedBy}
              updated={record?.Updated}
              updatedBy={record?.UpdatedBy}
            />
          )
        }>
        <div className="add-edit-place-page">
          <RMSMenuSidebar
            activeKey={activeKey}
            onSelect={handleMenuSelect}
            groups={PlaceSidebarGroups}
            className="app-sidebar--bordered"
            filterLabel="Filter place menu"
            renderItemLabel={(item) =>
              item.key === PlaceMenuItemKey.SOPS && sopCount > 0
                ? 'SOPs (' + sopCount + ')'
                : item.label
            }
            getItemClassName={(item) =>
              item.key === PlaceMenuItemKey.SOPS && sopCount > 0
                ? 'app-sidebar__item--alert'
                : undefined
            }
            disabled={busy}
          />
          <main
            className={`add-edit-place-page__content${
              isDetails ? '' : ' add-edit-place-page__content--grid'
            }`}>
            {record?.PlaceName && (
              <div className="add-edit-place-page__summary">{record.PlaceName}</div>
            )}
            {ActiveContent && (
              <ActiveContent
                key={activeKey}
                ref={isDetails ? detailsRef : undefined}
                ptsPlaceID={placeId}
                initialAddressId={isDetails ? initialAddressId : undefined}
                onStateChange={isDetails ? handleStateChange : undefined}
                onSaved={isDetails ? handleSaved : undefined}
                onSopsChanged={activeKey === PlaceMenuItemKey.SOPS ? handleSopsChanged : undefined}
              />
            )}
          </main>
        </div>
      </RMSCommonDialog>
      <RMSConfirmationDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        onYes={handleYes}
        onNo={handleNo}
      />
    </>
  );
};

export default AddEditPlaceDialog;
